import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import MDTypography from "ui/MDTypography";
import MDBox from "ui/MDBox";
import karkunService from "services/karkun-service";
import CircularProgress from "@mui/material/CircularProgress";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import { styled, keyframes } from "@mui/material/styles";

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const CardContainer = styled(MDBox)(({ theme }) => ({
  width: "90%",
  maxWidth: 450,
  padding: theme.spacing(4),
  borderRadius: theme.spacing(2),
  boxShadow: theme.shadows[6],
  backgroundColor: theme.palette.background.paper,
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  animation: `${fadeIn} 0.6s ease-out`,
}));

const StatusIcon = styled("div")(({ theme, status }) => ({
  marginBottom: theme.spacing(2),
  svg: {
    fontSize: 80,
    color:
      status === "success"
        ? theme.palette.success.main
        : status === "error"
        ? theme.palette.error.main
        : theme.palette.text.primary,
    animation: status === "loading" ? "spin 1s linear infinite" : "none",
  },
  "@keyframes spin": {
    "0%": { transform: "rotate(0deg)" },
    "100%": { transform: "rotate(360deg)" },
  },
}));

const KarkunVerifyDevice = () => {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState("loading");
  const [message, setMessage] = useState("Registering your device...");
  const [karkunName, setKarkunName] = useState("");

  useEffect(() => {
    const id = searchParams.get("id");
    if (!id) {
      setStatus("error");
      setMessage("❌ Invalid QR Code or missing ID.");
      return;
    }

    const macAddress = "N/A";

    karkunService
      .verifyDevice(id, { macAddress })
      .then((res) => {
        const data = res?.data || res;
        if (data?.success) {
          setStatus("success");
          setMessage(data.message || "✅ Your device has been registered successfully!");
          setKarkunName(data.karkun?.name || "");
        } else {
          setStatus("error");
          setMessage(data.message || "❌ Failed to register device.");
        }
      })
      .catch((err) => {
        console.error("Error verifying device:", err);
        setStatus("error");
        const backendMessage = err?.message || err?.response?.data?.message || "❌ Device verification failed.";
        setMessage(backendMessage);
      });
  }, [searchParams]);

  return (
    <MDBox
      display="flex"
      justifyContent="center"
      alignItems="center"
      height="100vh"
      sx={{ backgroundColor: "#f5f7fa" }}
    >
      <CardContainer>
        <StatusIcon status={status}>
          {status === "loading" && <CircularProgress size={80} />}
          {status === "success" && <CheckCircleIcon />}
          {status === "error" && <ErrorIcon />}
        </StatusIcon>

        <MDTypography variant={status === "success" ? "h4" : "h5"} fontWeight="medium" textAlign="center" gutterBottom>
          {message}
        </MDTypography>

        {status === "success" && karkunName && (
          <MDTypography variant="h6" color="textSecondary" textAlign="center">
            Welcome, {karkunName}!
          </MDTypography>
        )}
      </CardContainer>
    </MDBox>
  );
};

export default KarkunVerifyDevice;
